import React from "react";
import { Select, MenuItem } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import styles from "./index.module.css";

/**
 * CustomDropdown — a Select styled identically to the auth input boxes.
 *
 * Props:
 *  options      string[]   — list of options to display
 *  value        string     — currently selected value
 *  onChange     function   — MUI Select change handler: (event) => void
 *  placeholder  string     — placeholder shown when no value selected
 *  hasError     boolean    — highlights border in red
 *  className    string     — extra class applied to the root element
 */
const CustomDropdown = ({
  options = [],
  value,
  onChange,
  placeholder = "Select",
  hasError,
  className,
  compact = false,
}) => {
  const isObjectOptions = options.length > 0 && typeof options[0] === "object";

  const getAbbr = (label) => label?.match(/\(([^)]+)\)$/)?.[1] || label || "";

  return (
    <Select
      displayEmpty
      value={value || ""}
      onChange={onChange}
      className={[
        compact ? styles.rootCompact : styles.root,
        hasError ? styles.rootError : "",
        className || "",
      ]
        .filter(Boolean)
        .join(" ")}
      IconComponent={() => (
        <KeyboardArrowDownIcon
          className={compact ? styles.iconCompact : styles.icon}
        />
      )}
      renderValue={(selected) => {
        if (!selected)
          return <span className={styles.placeholder}>{placeholder}</span>;
        if (isObjectOptions) {
          const opt = options.find((o) => o.value === selected);
          return (
            <span className={styles.selectedRow}>
              {opt?.img && (
                <img src={opt.img} alt="" className={styles.flagImg} />
              )}
              <span
                className={compact ? styles.selectedCompact : styles.selected}
              >
                {getAbbr(opt?.label)}
              </span>
            </span>
          );
        }
        return <span className={styles.selected}>{selected}</span>;
      }}
      MenuProps={{
        PaperProps: {
          sx: {
            borderRadius: "12px",
            mt: "4px",
            boxShadow: "0px 8px 24px rgba(0,0,0,0.12)",
            maxHeight: "240px",
          },
        },
      }}
      sx={{
        "& .MuiOutlinedInput-notchedOutline": { border: "none" },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: "none" },
        "&:hover .MuiOutlinedInput-notchedOutline": { border: "none" },
        "& .MuiSelect-select": {
          padding: compact
            ? "0 8px 0 8px !important"
            : "0 40px 0 16px !important",
          display: "flex",
          alignItems: "center",
          height: "100% !important",
          boxSizing: "border-box",
        },
        "& .MuiSelect-icon": {
          top: "50%",
          transform: "translateY(-50%)",
          right: compact ? "6px" : "24px",
          color: "inherit",
        },
        fontFamily: "Inter, sans-serif",
        fontWeight: 600,
        fontSize: compact ? "14px" : "16px",
        letterSpacing: "-0.03em",
      }}
    >
      {options.map((opt) => {
        const key = isObjectOptions ? opt.value : opt;
        const val = isObjectOptions ? opt.value : opt;
        const label = isObjectOptions ? opt.label : opt;
        const img = isObjectOptions ? opt.img : null;
        return (
          <MenuItem
            key={key}
            value={val}
            sx={{
              fontFamily: "Inter, sans-serif",
              fontSize: "14px",
              fontWeight: 500,
              color: "#404040",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              "&:hover": { backgroundColor: "#FFF9EC" },
              "&.Mui-selected": { backgroundColor: "#FFF3CC", fontWeight: 600 },
              "&.Mui-selected:hover": { backgroundColor: "#FFE999" },
            }}
          >
            {img && <img src={img} alt="" className={styles.flagImg} />}
            {label}
          </MenuItem>
        );
      })}
    </Select>
  );
};

export default CustomDropdown;
